import type { ResetPasswordRequest } from 'services/generated/auth-service/models/ResetPasswordRequest';
import type { SignInRequest } from 'services/generated/auth-service/models/SignInRequest';
import type { TokenResponse } from 'services/generated/auth-service/models/TokenResponse';
import type { UserData } from 'services/generated/auth-service/models/UserData';
import { OpenAPI as AuthOpenApi } from 'services/generated/auth-service/core/OpenAPI';
import { WhoopAuthResourceV2Service as WhoopAuthV2 } from 'services/generated/auth-service/services/WhoopAuthResourceV2Service';
import { WhoopUsersResourceV2Service as WhoopUserV2 } from 'services/generated/auth-service/services/WhoopUsersResourceV2Service';
import * as Cookies from 'es-cookie';
import { AnalyticsBrowser } from '@segment/analytics-next';
import {
  removeItem,
  setItem,
  WHOOP_LS_AUTH_TOKEN,
  WHOOP_LS_USER,
} from './local-storage';

interface AuthProps {
  env: 'local' | 'dev' | 'prod';
  serverCookies?: string;
  segmentKey: string;
}

const WHOOP_AUTH_TOKEN = `whoop-auth-token`;
const WHOOP_AUTH_COOKIE_FROM_STORAGE = 'whoop.security.accessToken';
const WHOOP_REFRESH_TOKEN = `whoop-auth-refresh-token`;
const WHOOP_AUTH_EXPIRY = `whoop-auth-expiry`;
const WHOOP_AUTH_USER = `whoop-auth-user`;

// https://whoopinc.atlassian.net/browse/GRO-3512
// This is being replaced and should be removed in 3 months after
const WHOOP_LEGACY_IOS_COOKIE = `@whoopauth.accessToken`;
const WHOOP_ENCODED_LEGACY_IOS_COOKIE = `%40whoopauth.accessToken`;

const WHOOP_LEGACY_IOS_EXPIRY = `@whoopauth.accessToken.expires`;
const WHOOP_ENCODED_LEGACY_IOS_EXPIRY = `%40whoopauth.accessToken.expires`;

interface ErrorWithMessage {
  message: string;
}

interface SegmentTrack {
  eventName: string;
  category: 'sign_in_web_page' | 'forgot_password_web_page';
  action:
    | 'invalid_signin'
    | 'sign_in'
    | 'forgot_password_page_view'
    | 'resetPasswordV2Failed'
    | 'resetPasswordV2Success';
}

interface ClientAuth {
  isAuthenticated(): boolean;
  isExpired(): boolean;
  isRefreshing(): boolean;
  isAboutToExpire(): boolean;
  getToken(): string;
  getUser(): Promise<UserData | undefined>;
  signIn(request: SignInRequest): Promise<void>;
  signOut(): Promise<void>;
  refresh(): Promise<void>;
  resetPassword(request: {
    resetToken: string;
    newPassword: string;
  }): Promise<void>;
  sendPasswordEmail(request: { email: string; useV2?: boolean }): Promise<void>;
  lockout(token: string): Promise<string>;
}

class ClientAuth {
  #now = new Date();
  #authenticated = false;
  #token = '';
  #tokenExpiresAt = this.#now; // instantiate to expire right away
  #refreshToken = '';
  #refreshTokenExpiresAt = this.#now; // instantiate to expire right away
  segmentAnalytics: AnalyticsBrowser | null = null;
  #apiEnv = 'prod';

  ONE_HOUR = 60 * 60 * 1000;

  #isRefreshing = false;

  constructor({ env, segmentKey }: AuthProps) {
    const apiEnv = env === 'prod' ? 'prod' : 'dev';
    this.#apiEnv = apiEnv;

    AuthOpenApi.BASE = `https://api.${apiEnv}.whoop.com/auth-service`;
    this.loadAuthenticationData();
    if (this.isClientSide()) {
      this.initSegment(segmentKey);
    }
  }

  async initSegment(segmentKey: string) {
    this.segmentAnalytics = new AnalyticsBrowser();
    this.segmentAnalytics
      .load({
        writeKey: segmentKey,
      })
      .catch((error) => {
        console.error('There was a problem initiating segment', error);
      });
  }

  isClientSide() {
    return typeof window !== 'undefined';
  }

  getToken(): string {
    return this.#token;
  }

  async getUser(): Promise<UserData | undefined> {
    AuthOpenApi.BASE = `https://api.${this.#apiEnv}.whoop.com/auth-service`;
    AuthOpenApi.HEADERS = {
      Authorization: `Bearer ${this.#token}`,
    };
    let userResponse;
    try {
      userResponse = await WhoopUserV2.getUser1();
    } catch (error) {
      console.error('There was a problem initiating user', error);
    }
    return userResponse?.user;
  }

  trackEvent({ eventName, category, action }: SegmentTrack) {
    try {
      this.segmentAnalytics?.track(eventName, { category, action });
    } catch (error) {
      console.error('There was a problem tracking event', error);
    }
  }

  isAuthenticated(): boolean {
    return this.#authenticated;
  }

  cleanCookie = (cookie: string) => {
    const decodedToken = decodeURIComponent(cookie)?.replaceAll('"', '');
    try {
      // only some cookies are parseable
      const parsedToken = JSON.parse(decodedToken);
      return parsedToken;
    } catch (_error) {
      // if the token is not JSON parseable, keep it as is
      return decodedToken;
    }
  };

  loadLegacyToken(): void {
    const legacyToken =
      Cookies.get(WHOOP_LEGACY_IOS_COOKIE) ??
      Cookies.get(WHOOP_ENCODED_LEGACY_IOS_COOKIE) ??
      '';
    const legacyExpiry =
      Cookies.get(WHOOP_LEGACY_IOS_EXPIRY) ??
      Cookies.get(WHOOP_ENCODED_LEGACY_IOS_EXPIRY) ??
      '';

    if (legacyToken && legacyExpiry) {
      this.#token = this.cleanCookie(legacyToken);
      this.#tokenExpiresAt = new Date(this.cleanCookie(legacyExpiry));

      // Set auth state
      this.#authenticated = true;

      // Normalize the token and expiry
      Cookies.remove(WHOOP_LEGACY_IOS_COOKIE);
      Cookies.remove(WHOOP_ENCODED_LEGACY_IOS_COOKIE);
      Cookies.set(WHOOP_AUTH_TOKEN, this.#token, {
        expires: this.#tokenExpiresAt,
        secure: true,
      });
      Cookies.remove(WHOOP_LEGACY_IOS_EXPIRY);
      Cookies.remove(WHOOP_ENCODED_LEGACY_IOS_EXPIRY);
      Cookies.set(WHOOP_AUTH_EXPIRY, this.#tokenExpiresAt.toString(), {
        expires: this.#tokenExpiresAt,
        secure: true,
      });
    }
  }

  async loadAuthenticationData(): Promise<void> {
    if (!this.#authenticated && this.isClientSide()) {
      const authToken =
        Cookies.get(WHOOP_AUTH_TOKEN) ||
        Cookies.get(WHOOP_AUTH_COOKIE_FROM_STORAGE);
      this.#token = authToken ? this.cleanCookie(authToken) : '';
      if (!this.#token) {
        // attempt to load legacy token
        this.loadLegacyToken();
      }
      this.#refreshToken = Cookies.get(WHOOP_REFRESH_TOKEN) ?? '';
      this.#authenticated = this.#token.length > 0;

      if (this.#refreshToken && (this.isExpired() || this.isAboutToExpire())) {
        await this.refresh();
      }
    }
  }

  isExpired(): boolean {
    return this.#token === '';
  }

  isRefreshing(): boolean {
    return this.#isRefreshing;
  }

  isAboutToExpire(): boolean {
    // if within 1 hour of expiration
    const authExpiry = Cookies.get(WHOOP_AUTH_EXPIRY);
    if (authExpiry) {
      const expiresWithinHour =
        new Date(authExpiry).valueOf() - Date.now() < this.ONE_HOUR;
      return this.isAuthenticated() && expiresWithinHour;
    }
    return false;
  }

  setData(data: TokenResponse): void {
    this.#token = data.access_token ?? '';
    this.#refreshToken = data.refresh_token ?? '';
    this.#tokenExpiresAt = new Date(
      Date.now() + (data.access_token_expires_in ?? 0) * 1000,
    );
    this.#refreshTokenExpiresAt = new Date(
      Date.now() + (data.refresh_token_expires_in ?? 0) * 1000,
    );
    if (this.#token.length > 0) {
      this.#authenticated = true;
    }
  }

  determineDomain(): { domain: string; secure: boolean } {
    const currentHostname = window.location.hostname;
    const allowedDomains = [
      '.whoop-web-ecomm-jf.pages.dev',
      '.whoop-web-shop.pages.dev',
      '.whoop-web-web-app.pages.dev',
      '.whoop-web-trials.pages.dev',
      '.whoop-web-ui.pages.dev',
      '.whoop.workers.dev',
      '.dev.whoop.com',
    ];
    const COOKIE_SETTINGS = {
      local: {
        domain: 'localhost',
        secure: false,
      },
      dev: {
        domain: currentHostname.endsWith('.dev.whoop.com')
          ? '.dev.whoop.com'
          : currentHostname,
        secure: true,
      },
      prod: {
        domain: '.whoop.com',
        secure: true,
      },
    };
    // Safeguard
    if (!window) return COOKIE_SETTINGS.prod;
    if (allowedDomains.some((domain) => currentHostname.endsWith(domain))) {
      return COOKIE_SETTINGS.dev;
    } else if (currentHostname === 'localhost') {
      return COOKIE_SETTINGS.local;
    }
    return COOKIE_SETTINGS.prod;
  }

  async setCookies(): Promise<void> {
    const { domain, secure } = this.determineDomain();

    Cookies.set(WHOOP_AUTH_TOKEN, this.#token, {
      domain,
      secure,
      expires: this.#tokenExpiresAt,
    });
    Cookies.set(WHOOP_AUTH_EXPIRY, this.#tokenExpiresAt.toString(), {
      domain,
      secure,
      expires: this.#tokenExpiresAt,
    });
    Cookies.set(WHOOP_REFRESH_TOKEN, this.#refreshToken, {
      domain,
      secure,
      expires: this.#refreshTokenExpiresAt,
    });
    if (this.#authenticated) {
      const user = await this.getUser();
      Cookies.set(WHOOP_AUTH_USER, JSON.stringify(user), {
        secure,
        domain,
      });
    }
  }

  async setLocalStorage(): Promise<void> {
    setItem(WHOOP_LS_AUTH_TOKEN, this.#token);
    if (this.#authenticated) {
      const user = await this.getUser();
      setItem(WHOOP_LS_USER, JSON.stringify(user));
    }
  }

  clearCookies(): void {
    const { domain, secure } = this.determineDomain();
    Cookies.remove(WHOOP_AUTH_TOKEN, { domain, secure });
    Cookies.remove(WHOOP_REFRESH_TOKEN, { domain, secure });
    Cookies.remove(WHOOP_AUTH_EXPIRY, { domain, secure });
    Cookies.remove(WHOOP_AUTH_USER, { domain, secure });
    this.#token = '';
    this.#refreshToken = '';
    this.#authenticated = false;
  }

  clearLocalStorage(): void {
    removeItem(WHOOP_LS_AUTH_TOKEN);
    removeItem(WHOOP_LS_USER);
  }

  trackSignInPageView(): void {
    this.segmentAnalytics?.track('Auth Flow', {
      category: 'sign_in_web_page',
      action: 'viewed',
    });
  }

  async signIn({ username, password }: SignInRequest): Promise<void> {
    this.segmentAnalytics?.track('Auth Flow', {
      category: 'sign_in_web_page',
      action: 'sign_in',
    });
    try {
      const data = await WhoopAuthV2.signIn({
        requestBody: { username, password },
      });
      this.setData(data);
      await this.setCookies();
      await this.setLocalStorage();
    } catch (_error) {
      const error = _error as ErrorWithMessage;
      if (error?.message) {
        if (error.message.includes('401')) {
          // Attempt refresh if sign-in fails
          this.refresh();
        } else {
          this.segmentAnalytics?.track('Auth Flow', {
            category: 'sign_in_web_page',
            action: 'invalid_signin',
          });
          throw Error(`Failed to sign in: ${error.message}`);
        }
      }
    }
  }

  async refresh(): Promise<void> {
    if (!this.#refreshToken) return;
    try {
      this.#isRefreshing = true;
      const data = await WhoopAuthV2.refresh({
        authorization: `Bearer ${this.#refreshToken}`,
      });
      this.setData(data);
      await this.setCookies();
      await this.setLocalStorage();
      this.#isRefreshing = false;
    } catch (_error) {
      this.#isRefreshing = false;
      const error = _error as ErrorWithMessage;
      if (error?.message) {
        if (error.message.includes('401')) {
          // If refreshing fails with 401, sign the user out
          this.clearCookies();
          this.clearLocalStorage();
        } else {
          console.error('Error while refreshing token', error);
        }
      }
    }
  }

  async signOut(): Promise<void> {
    // Retain the refresh token for the API call
    const refreshToken = this.#refreshToken;
    // An intent to sign-out clears all local data irrespective of whether the API call succeeds or not
    this.clearCookies();
    this.clearLocalStorage();
    if (refreshToken) {
      try {
        await WhoopAuthV2.signOut({
          authorization: `Bearer ${refreshToken}`,
        });
      } catch (error) {
        console.error('There was a problem signing out', error);
      }
    }
  }

  async resetPassword({
    resetToken,
    newPassword,
  }: {
    resetToken: string;
    newPassword: string;
  }): Promise<void> {
    const body: ResetPasswordRequest = {
      new_password: newPassword,
    };
    await WhoopAuthV2.resetPassword1({
      authorization: `Bearer ${resetToken}`,
      requestBody: body,
    });
  }

  async sendPasswordEmail({
    email,
    useV2 = false,
  }: {
    email: string;
    useV2?: boolean;
  }): Promise<void> {
    await WhoopAuthV2.sendResetPasswordEmail({
      requestBody: { email },
      // eslint-disable-next-line id-length
      v2: useV2,
    });
  }

  async lockout(token: string): Promise<string> {
    try {
      this.segmentAnalytics?.track('Auth Flow', {
        category: 'lockout',
        action: 'lockout_initiated',
      });
      const location = await WhoopAuthV2.lockout({
        requestBody: {
          token: token,
        },
      });

      if (!location) {
        this.segmentAnalytics?.track('Auth Flow', {
          category: 'lockout',
          action: 'lockout_failed',
        });
        throw new Error('No Location header in response');
      }

      return location;
    } catch (error) {
      this.segmentAnalytics?.track('Auth Flow', {
        category: 'lockout',
        action: 'lockout_failed',
      });
      console.error('Error during lockout:', error);
      throw error;
    }
  }
}

let clientInstance: ClientAuth | null = null;

const createAuth = ({ env, segmentKey }: AuthProps) => {
  // Check if an instance already exists
  if (!clientInstance) {
    // If not, create a new instance and store it in authInstance
    clientInstance = new ClientAuth({ env, segmentKey });
  }
  // Return the existing or new instance
  return clientInstance;
};

export {
  createAuth as createClientAuth,
  type AuthProps,
  WHOOP_AUTH_TOKEN,
  WHOOP_AUTH_USER,
  WHOOP_REFRESH_TOKEN,
  WHOOP_AUTH_EXPIRY,
  WHOOP_LEGACY_IOS_COOKIE,
  WHOOP_LEGACY_IOS_EXPIRY,
  WHOOP_ENCODED_LEGACY_IOS_COOKIE,
};
